<script setup>
import { computed, ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import IconBase from '../icon/IconBase.vue';
import FolderStar from './FolderStar.vue';
import IconUpload from '../icon/IconUpload.vue';
import Tooltip from '../Atoms/Tooltip.vue';
import IconBin from '../icon/IconBin.vue';
import IconCross from '../icon/IconCross.vue';
import IconEdit from '../icon/IconEdit.vue';
import IconLock from '../icon/IconLock.vue';
import IconFolder from '../icon/IconFolder.vue';
import DropDownMenu from '../Atoms/DropDownMenu.vue';
import FormInput from '../Atoms/FormInput.vue';
import RemoveMenuItem from '../ui/RemoveMenuItem.vue';
import FormButton from '../Atoms/FormButton.vue';
import { folderTint, folderInitial } from '@/Utils/folderColor';
import { useFavorites } from '@/Utils/useFavorites';

const props = defineProps({
	folder: { type: Object, required: true },
	view: { type: String, default: 'card' }, // 'card' | 'list'
	recent: { type: Boolean, default: false }, // shown in the "Recent" section
	// Sub-folder inside a VOD folder (visual grouping): `folder` is then a
	// pseudo-folder ({ _id, name, parent, totalFiles }) and opening it stays on
	// the parent folder route with a ?subfolder= query.
	subfolder: { type: Boolean, default: false },
});

const formatBytes = (size) => {
	if (!size || size <= 0) return '0 B';
	const i = Math.floor(Math.log(size) / Math.log(1000));
	const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
	return `${+(size / Math.pow(1000, i)).toFixed(1)} ${units[i]}`;
};

const emit = defineEmits(['newVideo', 'rename', 'delete', 'removeRecent']);

const router = useRouter();
const { isFavorite, toggleFavorite } = useFavorites();

const tint = computed(() => folderTint(props.folder));
const initial = computed(() => folderInitial(props.folder));
// Only show the initial when it's a real letter/number; otherwise (names that
// start with punctuation like "?-m") fall back to a folder glyph.
const initialIsAlnum = computed(() => /^[A-Z0-9]$/i.test(initial.value));
const isLive = computed(() => !!props.folder?.liveParent);
const isProtected = computed(() => !!(props.folder?.config?.password && !props.folder?.config?.isPasswordDisabled));
const mediaCount = computed(() => props.folder?.totalFiles || 0);
const mediaLabel = computed(() => `${mediaCount.value} ${mediaCount.value === 1 ? 'video' : 'videos'}`);
const sizeText = computed(() => formatBytes(props.folder?.totalBytes));
const favorite = computed(() => isFavorite(props.folder?._id));

const updatedText = computed(() => {
	const raw = props.folder?.lastUpdated || props.folder?.creationTime;
	if (!raw) return '';
	const date = new Date(raw);
	if (isNaN(date.getTime())) return '';
	const diff = Date.now() - date.getTime();
	const day = 86400000;
	if (diff < 0) return '';
	if (diff < day) return 'today';
	if (diff < 2 * day) return 'yesterday';
	if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
	if (diff < 30 * day) return `${Math.floor(diff / (7 * day))}w ago`;
	const sameYear = date.getFullYear() === new Date().getFullYear();
	return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
});

const open = () => {
	// Don't navigate while renaming, nor on the click that just closed the
	// rename input (blur fires before click, so the flag alone isn't enough).
	if (renaming.value || renameCloseGuard.value) return;
	if (props.subfolder) {
		router.push({
			name: 'VODFolderManage',
			params: { streamId: props.folder.parent },
			query: { subfolder: props.folder._id },
		});
		return;
	}
	router.push({ name: 'VODFolderManage', params: { streamId: props.folder._id } });
};

const onToggleFavorite = () => toggleFavorite(props.folder?._id);

// Inline rename: pencil turns the card's name into an input (no popup)
const renaming = ref(false);
const renameValue = ref('');
const renameInput = ref(null);
// Swallows the click that dismissed the input so it can't open the folder
const renameCloseGuard = ref(false);
let renameGuardTimer = null;
const armRenameCloseGuard = () => {
	renameCloseGuard.value = true;
	clearTimeout(renameGuardTimer);
	renameGuardTimer = setTimeout(() => { renameCloseGuard.value = false; }, 350);
};
const startRename = () => {
	renaming.value = true;
	renameValue.value = props.folder?.name || '';
	nextTick(() => renameInput.value?.focus?.());
};
const cancelRename = () => {
	// Reset the draft so the unmount blur doesn't save discarded edits
	renameValue.value = props.folder?.name || '';
	renaming.value = false;
	armRenameCloseGuard();
};
const saveRename = () => {
	if (!renaming.value) return; // blur after Enter/Esc already handled it
	const name = renameValue.value.trim();
	renaming.value = false;
	armRenameCloseGuard();
	if (!name || name === props.folder?.name) return;
	emit('rename', props.folder, name);
};

// Trash icon: inline 2-step delete confirm (folder-appropriate copy)
const deleteMenu = ref(null);
const closeDeleteMenu = () => deleteMenu.value?.hide?.();
const deleteConfirmText = computed(() => mediaCount.value > 0
	? `Delete this folder and its ${mediaLabel.value}?`
	: 'Delete this folder?');
const onDeleteConfirm = () => {
	closeDeleteMenu();
	emit('delete', props.folder);
};
</script>

<template>
	<!-- CARD VIEW -->
	<div v-if="view === 'card'"
		class="folder-card group relative rounded-xl bg-surface-2 shadow-[0_1px_3px_rgba(0,0,0,0.15),0_10px_28px_-8px_rgba(0,0,0,0.4)] transition-[box-shadow,transform] duration-200 ease-out hover:shadow-[0_2px_6px_rgba(0,0,0,0.18),0_20px_44px_-10px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 cursor-pointer p-3.5 flex flex-col"
		@click="open">
		<div class="flex items-center gap-3">
			<div class="size-12 shrink-0 rounded-xl flex items-center justify-center" :style="{ background: tint.bg, color: tint.fg }">
				<span v-if="initialIsAlnum" class="text-xl font-semibold select-none">{{ initial }}</span>
				<icon-base v-else :width="22" :height="22" fill="none"><icon-folder /></icon-base>
			</div>
			<div class="min-w-0 flex-1">
				<div v-if="renaming" @click.stop>
					<FormInput ref="renameInput" v-model="renameValue"
						@keyup.enter="saveRename" @keyup.esc="cancelRename" @blur="saveRename" />
				</div>
				<template v-else>
					<div class="flex items-start gap-1.5 min-w-0">
						<!-- Sub-folder names stay on one line (truncate) per design -->
						<p class="text-base text-white font-semibold leading-snug min-w-0"
							:class="subfolder ? 'truncate' : 'line-clamp-2 break-words'"
							:title="folder.name">{{ folder.name }}</p>
						<icon-base v-if="isProtected" title="Password protected" class="shrink-0 text-surface-7 mt-1" :width="13" :height="13" fill="none">
							<icon-lock />
						</icon-base>
					</div>
					<p v-if="isLive" class="text-tiny text-gold font-medium truncate mt-0.5">Live Recordings</p>
				</template>
			</div>
			<!-- Recent section: a dismiss (×) to remove from Recent (clears localStorage entry) -->
			<FormButton v-if="recent" title="Remove from Recent"
				custom-classes="size-7 shrink-0 self-start -mt-1 -mr-1 rounded-md flex items-center justify-center text-surface-8 hover:text-white hover:bg-surface-4 transition opacity-0 group-hover:opacity-100"
				@click.stop="emit('removeRecent', folder)">
				<icon-base fill="none"><icon-cross /></icon-base>
			</FormButton>
			<!-- Star: top corner — it's state (pinned), not an action -->
			<FormButton v-else
				:custom-classes="'size-7 shrink-0 self-start -mt-1 -mr-1 rounded-md flex items-center justify-center text-surface-8 hover:bg-surface-4 transition ' + (favorite ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')"
				@click.stop="onToggleFavorite">
				<FolderStar :starred="favorite" :size="18" />
			</FormButton>
		</div>
		<!-- Footer: stats get the full row width (no truncation from the actions);
		     actions float as a hover overlay bottom-right instead of reserving space -->
		<div class="mt-auto pt-2">
			<span class="text-xs tabular-nums truncate block min-w-0">
				<span class="text-surface-9 font-medium">{{ mediaLabel }}</span>
				<span v-if="updatedText" class="text-surface-7"> &bull; Created {{ updatedText }}</span>
			</span>
		</div>
		<div class="absolute bottom-2 right-2 flex items-center gap-0.5 rounded-lg bg-surface-2 pl-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity before:content-[''] before:absolute before:right-full before:top-0 before:bottom-0 before:w-10 before:bg-gradient-to-l before:from-surface-2 before:to-transparent"
			@click.stop>
			<Tooltip content="Rename">
				<FormButton
					custom-classes="size-7 rounded-md flex items-center justify-center text-surface-8 hover:text-white hover:bg-surface-4 transition"
					@click.stop="startRename">
					<icon-base fill="none"><icon-edit /></icon-base>
				</FormButton>
			</Tooltip>
			<!-- Live-to-VOD folders are filled by recordings, not manual uploads. -->
			<Tooltip v-if="!isLive" content="Add video">
				<FormButton
					custom-classes="size-7 rounded-md flex items-center justify-center text-surface-8 hover:text-white hover:bg-surface-4 transition"
					@click.stop="emit('newVideo', folder, 'computer')">
					<icon-base fill="none"><icon-upload /></icon-base>
				</FormButton>
			</Tooltip>
			<DropDownMenu ref="deleteMenu" position="right" isIcon no-popper
				customDropdownClass="!bottom-8 !right-0"
				customClasses="h-7 w-7 rounded-md flex items-center justify-center text-surface-8 hover:text-error hover:bg-surface-4">
				<template #toggle-button>
					<icon-base fill="none">
						<icon-bin />
					</icon-base>
				</template>
				<template #menu-items>
					<RemoveMenuItem immediate :confirm-text="deleteConfirmText"
						@confirm="onDeleteConfirm" @cancel="closeDeleteMenu" />
				</template>
			</DropDownMenu>
		</div>
	</div>

	<!-- LIST VIEW -->
	<div v-else
		class="folder-row group flex min-w-0 items-center gap-3 bg-surface-2 hover:bg-surface-3 rounded-lg px-3 py-2 cursor-pointer transition"
		@click="open">
		<FormButton v-if="recent" title="Remove from Recent" custom-classes="shrink-0 flex items-center justify-center w-6 text-surface-8 hover:text-white"
			@click.stop="emit('removeRecent', folder)">
			<icon-base fill="none"><icon-cross /></icon-base>
		</FormButton>
		<FormButton v-else custom-classes="shrink-0 flex items-center justify-center w-6" @click.stop="onToggleFavorite">
			<FolderStar :starred="favorite" :size="20" />
		</FormButton>
		<div class="size-9 rounded-lg flex items-center justify-center shrink-0" :style="{ background: tint.bg, color: tint.fg }">
			<span v-if="initialIsAlnum" class="text-sm font-semibold select-none">{{ initial }}</span>
			<icon-base v-else :width="16" :height="16" fill="none"><icon-folder /></icon-base>
		</div>
		<div class="grid min-w-0 flex-1 grid-cols-1">
			<div v-if="renaming" @click.stop class="max-w-[280px]">
				<FormInput ref="renameInput" v-model="renameValue"
					@keyup.enter="saveRename" @keyup.esc="cancelRename" @blur="saveRename" />
			</div>
			<div v-else class="flex items-center gap-1.5 min-w-0">
				<p class="min-w-0 text-sm text-white font-medium truncate" :title="folder.name">{{ folder.name }}</p>
				<icon-base v-if="isProtected" title="Password protected" class="shrink-0 text-surface-7" :width="13" :height="13" fill="none">
					<icon-lock />
				</icon-base>
			</div>
			<p class="text-tiny truncate leading-tight mt-0.5 sm:hidden">
				<span v-if="isLive" class="text-gold">Live Recordings</span>
				<span v-if="isLive" class="text-surface-7"> &bull; </span>
				<span class="text-surface-7">{{ mediaLabel }} &bull; {{ sizeText }}</span>
			</p>
		</div>
		<div class="hidden md:flex w-[130px] shrink-0 items-center">
			<span v-if="isLive" class="inline-flex items-center gap-1 text-tiny font-medium text-gold">
				<span class="size-1.5 rounded-full bg-gold"></span>Live Recordings
			</span>
			<span v-else class="inline-flex items-center gap-1 text-tiny font-medium text-surface-7">
				<icon-base :width="13" :height="13" fill="none"><icon-folder /></icon-base>Folder
			</span>
		</div>
		<div class="hidden sm:block w-[70px] shrink-0 text-tiny text-surface-7 text-right tabular-nums">{{ mediaCount }}</div>
		<div class="hidden sm:block w-[80px] shrink-0 text-tiny text-surface-7 text-right tabular-nums">{{ sizeText }}</div>
		<div class="hidden lg:block w-[90px] shrink-0 text-tiny text-surface-7 text-right">{{ updatedText }}</div>
		<div @click.stop class="shrink-0 w-14 flex items-center justify-end">
			<Tooltip content="Rename">
				<FormButton
					custom-classes="size-7 rounded-md flex items-center justify-center text-surface-8 hover:text-white hover:bg-surface-4 transition opacity-0 group-hover:opacity-100"
					@click.stop="startRename">
					<icon-base fill="none"><icon-edit /></icon-base>
				</FormButton>
			</Tooltip>
			<div>
				<DropDownMenu ref="deleteMenu" position="right" isIcon no-popper
					customDropdownClass="!top-8 !right-0"
					customClasses="h-7 w-7 rounded-md flex items-center justify-center text-surface-8 hover:text-error hover:bg-surface-4">
					<template #toggle-button>
						<icon-base fill="none">
							<icon-bin />
						</icon-base>
					</template>
					<template #menu-items>
						<RemoveMenuItem immediate :confirm-text="deleteConfirmText"
							@confirm="onDeleteConfirm" @cancel="closeDeleteMenu" />
					</template>
				</DropDownMenu>
			</div>
		</div>
	</div>
</template>
